import { useState } from 'react'
import { useAuth } from '../../lib/auth'
import { useToast } from '../../hooks/useToast'
import { useSharedTeam } from '../../hooks/useTeam'
import {
  useTeamMembers,
  useTeamInvitations,
  useBulkInviteTeamMembers,
  useRevokeInvitation,
  useRemoveTeamMember,
  useLeaveTeam,
  useTransferOwnership,
  useSetTeamMemberRole,
  useAcceptInvitation,
  type BulkInviteRow,
  type TeamInvitationRow,
  type TeamMemberRow,
  type AssignableRole,
} from '../../hooks/useTeamMembers'
import type { TeamRole } from '../../hooks/useTeam'
import { nameFromEmail } from '../../lib/nameFromEmail'
import {
  usePendingTeamInvitations,
  useJoinableTeams,
  useRequestToJoin,
  type JoinableTeam,
  type PendingTeamInvitation,
} from '../../hooks/useDiscovery'
import {
  useTeamDomains,
  useSetTeamAutoJoin,
} from '../../hooks/useTeamDomains'
import {
  useTeamJoinRequests,
  useApproveJoinRequest,
  useDeclineJoinRequest,
  type TeamJoinRequestWithUser,
} from '../../hooks/useTeamJoinRequests'

/**
 * Settings → Team panel.
 *
 * Kondo-style single-shared-team model: a user either has no shared team (in
 * which case we show a "create a team" form) or exactly one shared team.
 * Owners see invite + member-management controls; members see a read-only
 * roster plus a Leave button.
 *
 * Invite flow: we don't send email. The owner fills in the invitee's email,
 * the row lands in `team_invitations` with a random token, and the UI
 * surfaces a copy-to-clipboard invite URL of the form
 * `<origin>/?invite=<token>`. The recipient opens that URL in an authed
 * browser and the `?invite` handler in App calls `accept_team_invitation`.
 */
export default function TeamSettings() {
  const { user } = useAuth()
  const sharedTeam = useSharedTeam()

  const { toast, show: showToast } = useToast()

  return (
    <div className="relative flex flex-col gap-6">
      <header>
        <h1 className="text-lg font-semibold text-zinc-900">Team</h1>
        <p className="text-sm text-zinc-500">
          {sharedTeam
            ? 'Manage members and invitations for your shared team.'
            : 'Your team will appear here once you join one.'}
        </p>
      </header>

      {sharedTeam ? (
        <TeamPanel
          teamId={sharedTeam.id}
          teamName={sharedTeam.name}
          teamAutoJoinByDomain={sharedTeam.auto_join_by_domain ?? false}
          currentUserId={user?.id ?? ''}
          onToast={showToast}
        />
      ) : (
        <NoTeamPanel onToast={showToast} />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-zinc-900 px-4 py-2.5 shadow-lg">
          <span className="text-xs text-white">{toast}</span>
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// No-team state.
//
// Post-2026-05-16 a user cannot create a shared team directly — the signup
// trigger claims one for them on their first corporate-domain login. So the
// "create a team" form is gone. Two priority-ordered surfaces for a user
// who isn't in a shared team yet:
//   1. Pending invitations addressed to auth.email() — one-click accept.
//   2. Teams whose claimed domain matches auth.email() — one-click join
//      (auto_join) or request-to-join (discoverable).
//
// When both are empty we render an informational card. Two shapes:
//   - Webmail user (gmail/outlook/etc): teams require a work email, hard
//     stop. No path forward from here.
//   - Corporate user with no match yet: should be very rare (signup trigger
//     would have placed them already). Likely a transient lag — show a
//     "your team will appear here" holding message rather than the
//     previous create-team form.
// ────────────────────────────────────────────────────────────────────────────

function NoTeamPanel({ onToast }: { onToast: (msg: string) => void }) {
  const { user } = useAuth()
  const { invitations, loading: invitesLoading } = usePendingTeamInvitations()
  const { teams: joinable, loading: joinableLoading } = useJoinableTeams()
  const hasInvites = invitations.length > 0
  const hasJoinable = joinable.length > 0

  const email = user?.email ?? ''
  const domain = email.includes('@') ? email.split('@')[1].toLowerCase() : ''
  const onWebmail = isWebmailDomain(domain)

  return (
    <>
      {hasInvites && (
        <PendingInvitesPanel invitations={invitations} onToast={onToast} />
      )}
      {hasJoinable && (
        <JoinableTeamsPanel teams={joinable} onToast={onToast} />
      )}
      {/* Loading shimmer — first fetch only, and only when nothing else
          is visible. Keeps the "no team" → "has team" transition snappy on
          warm caches. */}
      {(invitesLoading || joinableLoading) && !hasInvites && !hasJoinable && (
        <section className="rounded-xl border border-zinc-200 bg-white p-6">
          <p className="text-xs text-zinc-400">Looking for teams you can join…</p>
        </section>
      )}
      {!invitesLoading && !joinableLoading && !hasInvites && !hasJoinable && (
        <section className="rounded-xl border border-zinc-200 bg-white p-6">
          <h2 className="text-sm font-semibold text-zinc-900">
            {onWebmail ? 'Teams are for work emails' : 'Looking for your team'}
          </h2>
          <p className="mt-1 text-xs text-zinc-500">
            {onWebmail
              ? 'Teams are only available on corporate email domains. To create a team, sign up with your work email.'
              : 'Your team will appear here once signup completes. Refresh in a moment — if it still doesn\'t show up, reach out to support.'}
          </p>
        </section>
      )}
    </>
  )
}

/**
 * Client-side webmail check. The authoritative list lives in the
 * database (_is_webmail_domain), but the no-team panel only needs to
 * decide which copy to render and we don't want to add a round-trip for
 * an aesthetic choice. Keep this list short — the top-N public providers
 * the vast majority of sign-ups come from. A false negative here shows
 * the "looking for your team" copy to a webmail user, which is a mild
 * UX miss but not a security issue — the server still refuses to place
 * them in a team.
 */
function isWebmailDomain(domain: string): boolean {
  const webmail = new Set([
    'gmail.com',
    'googlemail.com',
    'outlook.com',
    'hotmail.com',
    'live.com',
    'yahoo.com',
    'icloud.com',
    'me.com',
    'mac.com',
    'aol.com',
    'proton.me',
    'protonmail.com',
    'fastmail.com',
    'yandex.com',
    'mail.com',
  ])
  return webmail.has(domain)
}

function PendingInvitesPanel({
  invitations,
  onToast,
}: {
  invitations: PendingTeamInvitation[]
  onToast: (msg: string) => void
}) {
  const accept = useAcceptInvitation()
  const [pendingId, setPendingId] = useState<string | null>(null)

  const handleAccept = async (inv: PendingTeamInvitation) => {
    setPendingId(inv.invitation_id)
    try {
      const result = await accept.mutateAsync(inv.token)
      onToast(
        result.already_member
          ? `You're already in ${result.team_name}`
          : `Joined ${result.team_name}`,
      )
    } catch (err) {
      onToast(`Invite failed: ${(err as Error).message}`)
    } finally {
      setPendingId(null)
    }
  }

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6">
      <h2 className="text-sm font-semibold text-zinc-900">You've been invited</h2>
      <p className="mt-0.5 text-xs text-zinc-500">
        Accept an invitation to join a shared team.
      </p>
      <ul className="mt-3 divide-y divide-zinc-100">
        {invitations.map((inv) => {
          const inviter = inv.invited_by_name || inv.invited_by_email || 'A teammate'
          const isBusy = pendingId === inv.invitation_id && accept.isPending
          return (
            <li key={inv.invitation_id} className="flex items-center justify-between py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-zinc-900">{inv.team_name}</p>
                <p className="mt-0.5 text-[11px] text-zinc-400">
                  Invited by {inviter}
                </p>
              </div>
              <button
                onClick={() => handleAccept(inv)}
                disabled={isBusy}
                className="rounded-md bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-40"
              >
                {isBusy ? 'Accepting…' : 'Accept'}
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function JoinableTeamsPanel({
  teams,
  onToast,
}: {
  teams: JoinableTeam[]
  onToast: (msg: string) => void
}) {
  const request = useRequestToJoin()
  const [pendingId, setPendingId] = useState<string | null>(null)

  const handleJoin = async (team: JoinableTeam) => {
    setPendingId(team.team_id)
    try {
      const row = await request.mutateAsync(team.team_id)
      if (row.decision === 'approved') {
        onToast(`Joined ${team.team_name}`)
      } else {
        onToast(`Request sent — ${team.team_name}'s owner will review it`)
      }
    } catch (err) {
      onToast(`Request failed: ${(err as Error).message}`)
    } finally {
      setPendingId(null)
    }
  }

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6">
      <h2 className="text-sm font-semibold text-zinc-900">Teams at your company</h2>
      <p className="mt-0.5 text-xs text-zinc-500">
        These teams match your email domain.
      </p>
      <ul className="mt-3 divide-y divide-zinc-100">
        {teams.map((t) => {
          const isBusy = pendingId === t.team_id && request.isPending
          const label = t.has_pending_request
            ? 'Request pending'
            : t.auto_join_by_domain
              ? 'Join'
              : 'Request to join'
          const disabled = t.has_pending_request || isBusy
          return (
            <li key={t.team_id} className="flex items-center justify-between py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-zinc-900">{t.team_name}</p>
                <p className="mt-0.5 text-[11px] text-zinc-400">
                  {t.member_count} {t.member_count === 1 ? 'member' : 'members'}
                </p>
              </div>
              <button
                onClick={() => handleJoin(t)}
                disabled={disabled}
                className="rounded-md bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-40"
              >
                {isBusy ? 'Joining…' : label}
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Has-team state: members + invites + leave
// ────────────────────────────────────────────────────────────────────────────

function TeamPanel({
  teamId,
  teamName,
  teamAutoJoinByDomain,
  currentUserId,
  onToast,
}: {
  teamId: string
  teamName: string
  teamAutoJoinByDomain: boolean
  currentUserId: string
  onToast: (msg: string) => void
}) {
  const { members, loading: membersLoading } = useTeamMembers(teamId)
  const { invitations, loading: invitationsLoading } = useTeamInvitations(teamId)
  const { domains, loading: domainsLoading } = useTeamDomains(teamId)
  const bulkInvite = useBulkInviteTeamMembers(teamId)
  const revoke = useRevokeInvitation(teamId)
  const remove = useRemoveTeamMember(teamId)
  const leave = useLeaveTeam()
  const transfer = useTransferOwnership(teamId)
  const setRole = useSetTeamMemberRole(teamId)
  const { requests: joinRequests } = useTeamJoinRequests(teamId)
  const approveRequest = useApproveJoinRequest(teamId)
  const declineRequest = useDeclineJoinRequest(teamId)

  const myRole = members.find((m) => m.user_id === currentUserId)?.role ?? null
  const isOwner = myRole === 'owner'
  const isAdmin = myRole === 'admin'
  // "Can manage" = owner OR admin. Invites, pending-invitation management,
  // and member removal (subject to per-target rules) are all covered by
  // this — the UI gates that previously read `isOwner` now read this.
  // Role changes + ownership transfer + domain management remain
  // owner-only; those still read `isOwner` explicitly.
  const canManage = isOwner || isAdmin

  // Textarea contents. Stays editable during sends — the user can paste
  // another batch while the first is still in flight. The parsed emails
  // are derived from this on every keystroke (cheap).
  const [inviteText, setInviteText] = useState('')
  // Append-only results log. Each click of "Send invites" adds rows; the
  // Clear button resets it. Rows update in place as the mutation resolves.
  const [resultRows, setResultRows] = useState<BulkInviteRow[]>([])
  const [confirmLeave, setConfirmLeave] = useState(false)

  // Emails recognised in the textarea right now. Drives the "N emails
  // recognised" counter + the Send-button enabled state. We deliberately
  // don't short-circuit already-member / duplicate-pending here — the RPC
  // returns precise per-email errors and the UI renders them in-line.
  const parsedEmails = parseEmailsFromText(inviteText)

  const handleSendBulk = async () => {
    if (parsedEmails.length === 0) return

    // Seed placeholder rows for anything not already in the results list.
    // If the same email was submitted in a prior batch (e.g. to retry a
    // failure), we replace the existing row rather than duplicating.
    const emailsToSend = parsedEmails.filter(
      (email) =>
        !resultRows.some(
          (r) =>
            r.email === email &&
            (r.status === 'sending' || r.status === 'emailed' || r.status === 'sent'),
        ),
    )
    if (emailsToSend.length === 0) {
      onToast('All of those are already sent or in flight')
      setInviteText('')
      return
    }

    setResultRows((prev) => {
      const withoutRetries = prev.filter((r) => !emailsToSend.includes(r.email))
      const seeded: BulkInviteRow[] = emailsToSend.map((email) => ({
        email,
        status: 'pending',
        token: null,
        errorCode: null,
        emailError: null,
      }))
      return [...withoutRetries, ...seeded]
    })
    // Clear the parsed emails from the textarea so the user can paste more
    // without re-sending these. Anything unparsable stays put — if they
    // typed "alice@acme.com and hold on, let me find the others..." the
    // prose remains and the email is gone.
    setInviteText((text) => stripEmailsFromText(text, emailsToSend))

    try {
      // While useTeamDomains is still loading, `domains` is an empty array
      // — passing that as the pre-filter list would mean "drop everything
      // as off-domain". Pass `undefined` to skip the client-side filter and
      // let the server's domain gate speak authoritatively. Once the hook
      // resolves we apply the fast path.
      const registered = domainsLoading
        ? undefined
        : domains.map((d) => d.domain.toLowerCase())
      const outcome = await bulkInvite.invite(
        emailsToSend,
        (email, patch) => {
          setResultRows((prev) =>
            prev.map((r) => (r.email === email ? { ...r, ...patch } : r)),
          )
        },
        registered,
      )
      // Drop seed rows for emails we filtered out client-side so the
      // results log only shows actual send attempts. Surface the count
      // via a toast — a single summary line beats N red rows all saying
      // the same thing.
      if (outcome.offDomain.length > 0) {
        const offSet = new Set(outcome.offDomain)
        setResultRows((prev) => prev.filter((r) => !offSet.has(r.email)))
        onToast(
          outcome.offDomain.length === 1
            ? `Skipped ${outcome.offDomain[0]} — not on a registered domain`
            : `Skipped ${outcome.offDomain.length} email${outcome.offDomain.length === 1 ? '' : 's'} — not on a registered domain`,
        )
      }
    } catch (err) {
      // `invite` only throws on auth/teamId setup failures — per-row errors
      // are surfaced via onRowUpdate, not thrown. A throw here is rare and
      // catastrophic (no team, signed out); a toast is appropriate.
      onToast(`Failed to send invites: ${(err as Error).message}`)
    }
  }

  const handleApproveRequest = async (reqId: string, label: string) => {
    try {
      await approveRequest.mutateAsync(reqId)
      onToast(`Admitted ${label}`)
    } catch (err) {
      onToast(`Failed to admit: ${(err as Error).message}`)
    }
  }

  const handleDeclineRequest = async (reqId: string, label: string) => {
    try {
      await declineRequest.mutateAsync(reqId)
      onToast(`Declined ${label}`)
    } catch (err) {
      onToast(`Failed to decline: ${(err as Error).message}`)
    }
  }

  const handleClearResults = () => setResultRows([])

  const handleCopyResultLink = async (row: BulkInviteRow) => {
    if (!row.token) return
    await copyInviteLink(row.token)
    onToast(`Link copied for ${row.email}`)
  }

  const handleCopy = async (inv: TeamInvitationRow) => {
    await copyInviteLink(inv.token)
    onToast('Invite link copied')
  }

  const handleRevoke = async (inv: TeamInvitationRow) => {
    try {
      await revoke.mutateAsync(inv.id)
      onToast(`Revoked invite for ${inv.email}`)
    } catch (err) {
      onToast(`Failed to revoke: ${(err as Error).message}`)
    }
  }

  const handleRemove = async (m: TeamMemberRow) => {
    if (!confirm(`Remove ${m.display_name || m.email} from ${teamName}?`)) return
    try {
      await remove.mutateAsync(m.user_id)
      onToast('Member removed')
    } catch (err) {
      onToast(`Failed to remove: ${(err as Error).message}`)
    }
  }

  const handleSetRole = async (m: TeamMemberRow, role: AssignableRole) => {
    // Less destructive than transfer — no confirm dialog. The audit log
    // captures from_role/to_role; the owner can reverse via the same menu
    // in one click if they misclick.
    try {
      await setRole.mutateAsync({ userId: m.user_id, role })
      onToast(
        role === 'admin'
          ? `${m.display_name || m.email} is now an admin`
          : `${m.display_name || m.email} is now a member`,
      )
    } catch (err) {
      onToast(`Failed to update role: ${(err as Error).message}`)
    }
  }

  const handleTransfer = async (m: TeamMemberRow) => {
    // Intentional capital-T "Transfer" — this is a destructive role change
    // for the current user (demotes them to member). Confirm explicitly so
    // an accidental click on the Members row doesn't silently relinquish
    // ownership.
    if (!confirm(
      `Make ${m.display_name || m.email} the owner of ${teamName}?\n\n` +
      `You'll stay as a member. You can leave afterwards if you want.`,
    )) return
    try {
      await transfer.mutateAsync(m.user_id)
      onToast(`${m.display_name || m.email} is now the owner`)
    } catch (err) {
      onToast(`Failed to transfer ownership: ${(err as Error).message}`)
    }
  }

  const handleLeave = async () => {
    try {
      await leave.mutateAsync(teamId)
      // When the caller is the sole member, `leave_team` deletes the team
      // and cascades through team_members / team_invitations / templates.
      // Distinct toast so the user understands their action had a wider
      // effect than "just leaving".
      onToast(soleMember ? `Deleted ${teamName}` : `Left ${teamName}`)
      setConfirmLeave(false)
    } catch (err) {
      onToast(`Failed to leave: ${(err as Error).message}`)
    }
  }

  // Owner semantics:
  //   - Sole member? Leave = delete the team (cascades, confirmation shows
  //     the stronger copy). The `leave_team` RPC handles the delete path
  //     server-side.
  //   - Multi-member owner? Must transfer ownership first — the RPC
  //     rejects last-owner leaves with `last_owner_must_transfer`. We
  //     disable the Leave button pre-emptively and expose Make-owner
  //     buttons on each other member's row.
  //   - Non-owner? Normal leave.
  const otherMembers = members.filter((m) => m.user_id !== currentUserId)
  const soleMember = otherMembers.length === 0
  const ownerMustTransfer = isOwner && !soleMember

  return (
    <>
      {/* Team header */}
      <section className="rounded-xl border border-zinc-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">{teamName}</h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              {members.length} {members.length === 1 ? 'member' : 'members'}
              {myRole && ` · You're ${roleArticleLabel(myRole)}`}
            </p>
          </div>
        </div>
      </section>

      {/* Invite (owners + admins) */}
      {canManage && (
        <section className="rounded-xl border border-zinc-200 bg-white p-6">
          <h2 className="text-sm font-semibold text-zinc-900">Invite teammates</h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            Paste emails separated by commas, newlines, or spaces. We'll send
            each an invite and derive names from addresses automatically.
          </p>
          <textarea
            value={inviteText}
            onChange={(e) => setInviteText(e.target.value)}
            placeholder="alice@acme.com, bob@acme.com&#10;chris@acme.com"
            rows={4}
            className="mt-3 w-full resize-y rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm leading-5 focus:border-zinc-400 focus:outline-none"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-xs text-zinc-500">
              {parsedEmails.length === 0
                ? 'No emails recognised yet'
                : `${parsedEmails.length} email${parsedEmails.length === 1 ? '' : 's'} recognised`}
            </span>
            <button
              type="button"
              onClick={handleSendBulk}
              disabled={parsedEmails.length === 0}
              className="rounded-md bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-40"
            >
              Send invites
            </button>
          </div>

          {/* Results log — persistent across multiple submits. Updates in
              place as each row transitions pending → sending → terminal. */}
          {resultRows.length > 0 && (
            <div className="mt-5 border-t border-zinc-100 pt-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Results
                </h3>
                <button
                  type="button"
                  onClick={handleClearResults}
                  className="text-[11px] font-medium text-zinc-500 hover:text-zinc-800"
                >
                  Clear
                </button>
              </div>
              <ul className="divide-y divide-zinc-100">
                {resultRows.map((row) => (
                  <BulkInviteResultRow
                    key={row.email}
                    row={row}
                    onCopyLink={() => handleCopyResultLink(row)}
                  />
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Pending join requests (owners + admins). Rendered only when the
          team has pending rows — the table is empty for teams on
          auto-join-by-domain + teams where nobody has corporate-signed-up
          recently, which is most teams most of the time. Hide to avoid
          perma-empty visual noise. */}
      {canManage && joinRequests.length > 0 && (
        <section className="rounded-xl border border-zinc-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-zinc-900">
              Pending requests
            </h2>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
              {joinRequests.length}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-zinc-500">
            New signups on your team's domain waiting to be admitted.
          </p>
          <ul className="mt-3 divide-y divide-zinc-100">
            {joinRequests.map((req) => (
              <JoinRequestRow
                key={req.id}
                req={req}
                disabled={approveRequest.isPending || declineRequest.isPending}
                onApprove={(label) => handleApproveRequest(req.id, label)}
                onDecline={(label) => handleDeclineRequest(req.id, label)}
              />
            ))}
          </ul>
        </section>
      )}

      {/* Pending invitations (owners + admins) */}
      {canManage && (
        <section className="rounded-xl border border-zinc-200 bg-white p-6">
          <h2 className="text-sm font-semibold text-zinc-900">Pending invitations</h2>
          {invitationsLoading ? (
            <p className="mt-2 text-xs text-zinc-400">Loading...</p>
          ) : invitations.length === 0 ? (
            <p className="mt-2 text-xs text-zinc-400">No pending invitations.</p>
          ) : (
            <ul className="mt-3 divide-y divide-zinc-100">
              {invitations.map((inv) => {
                const expired = new Date(inv.expires_at).getTime() < Date.now()
                // Derived name is best-effort display only — the invitee's
                // real `profiles.display_name` replaces it on signup. When
                // nothing sensible can be derived (e.g. `123@acme.com`),
                // we fall through to showing the email as primary only.
                const derivedName = nameFromEmail(inv.email)
                return (
                  <li key={inv.id} className="flex items-center justify-between py-2.5">
                    <div className="min-w-0 flex-1">
                      {derivedName ? (
                        <>
                          <p className="truncate text-sm text-zinc-900">{derivedName}</p>
                          <p className="mt-0.5 truncate text-[11px] text-zinc-400">
                            {inv.email} ·{' '}
                            {expired
                              ? 'Expired'
                              : `expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="truncate text-sm text-zinc-900">{inv.email}</p>
                          <p className="mt-0.5 text-[11px] text-zinc-400">
                            {expired
                              ? 'Expired'
                              : `Expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                          </p>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!expired && (
                        <button
                          onClick={() => handleCopy(inv)}
                          className="rounded-md border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
                        >
                          Copy link
                        </button>
                      )}
                      <button
                        onClick={() => handleRevoke(inv)}
                        disabled={revoke.isPending}
                        className="rounded-md border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
                      >
                        Revoke
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}

      {/* Members */}
      <section className="rounded-xl border border-zinc-200 bg-white p-6">
        <h2 className="text-sm font-semibold text-zinc-900">Members</h2>
        {membersLoading ? (
          <p className="mt-2 text-xs text-zinc-400">Loading...</p>
        ) : (
          <ul className="mt-3 divide-y divide-zinc-100">
            {members.map((m) => {
              const isMe = m.user_id === currentUserId
              // Remove gates:
              //   owner → can remove admins and members (not self, not
              //           another owner — the last-owner guard on the RPC
              //           fires if they try).
              //   admin → can remove members only (not admins, not owner).
              //           Matches `admin_cannot_remove_non_member` on the RPC.
              const canRemove =
                !isMe &&
                m.role !== 'owner' &&
                (isOwner || (isAdmin && m.role === 'member'))
              // Promote / demote: owner-only, and only sensible for
              // non-owner targets (transferring to owner is its own button).
              const canSetAdmin = isOwner && !isMe && m.role === 'member'
              const canSetMember = isOwner && !isMe && m.role === 'admin'
              // Make-owner is the full "hand the keys over" transfer. Owner
              // only; any non-owner target is valid.
              const canMakeOwner = isOwner && !isMe && m.role !== 'owner'
              return (
                <li key={m.user_id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm text-zinc-900">
                        {m.display_name || m.email}
                        {isMe && <span className="ml-1.5 text-xs text-zinc-400">(you)</span>}
                      </p>
                      <RolePill role={m.role} />
                    </div>
                    <p className="mt-0.5 text-[11px] text-zinc-400">
                      {m.email}
                      {' · Joined '}
                      {new Date(m.joined_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    {canSetAdmin && (
                      <button
                        onClick={() => handleSetRole(m, 'admin')}
                        disabled={setRole.isPending}
                        className="rounded-md border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
                      >
                        Make admin
                      </button>
                    )}
                    {canSetMember && (
                      <button
                        onClick={() => handleSetRole(m, 'member')}
                        disabled={setRole.isPending}
                        className="rounded-md border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
                      >
                        Revoke admin
                      </button>
                    )}
                    {canMakeOwner && (
                      <button
                        onClick={() => handleTransfer(m)}
                        disabled={transfer.isPending}
                        className="rounded-md border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
                      >
                        Make owner
                      </button>
                    )}
                    {canRemove && (
                      <button
                        onClick={() => handleRemove(m)}
                        disabled={remove.isPending}
                        className="rounded-md border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {/* Domains section. Read-only list for all members; the
          auto-join toggle is gated to owner. Domains themselves are
          claimed by the signup trigger — no add/remove surface here. */}
      <DomainsSection
        teamId={teamId}
        isOwner={isOwner}
        autoJoinByDomain={teamAutoJoinByDomain}
        onToast={onToast}
      />


      {/* Leave / delete team. Three branches:
           1. Owner + other members → must transfer first (Leave disabled,
              copy points at the Make-owner buttons above).
           2. Owner + sole member → Leave deletes the team (cascade).
              Copy is stronger so the user knows it's destructive.
           3. Non-owner member → normal leave. */}
      <section className="rounded-xl border border-red-200 bg-red-50/30 p-6">
        <h2 className="text-sm font-semibold text-red-700">
          {soleMember && isOwner ? 'Delete team' : 'Leave team'}
        </h2>
        <p className="mt-0.5 text-xs text-red-600/80">
          {ownerMustTransfer
            ? "You're the owner — transfer ownership to a member above, or remove everyone else first."
            : soleMember && isOwner
              ? `You're the only member. Leaving will permanently delete ${teamName} and all of its shared snippets. Your personal snippets stay with you.`
              : `You'll lose access to ${teamName}'s shared snippets. Your personal snippets stay with you.`}
        </p>
        {confirmLeave ? (
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={handleLeave}
              disabled={leave.isPending || ownerMustTransfer}
              className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-40"
            >
              {leave.isPending
                ? (soleMember && isOwner ? 'Deleting...' : 'Leaving...')
                : (soleMember && isOwner ? `Yes, delete ${teamName}` : `Yes, leave ${teamName}`)}
            </button>
            <button
              onClick={() => setConfirmLeave(false)}
              className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmLeave(true)}
            disabled={ownerMustTransfer}
            className="mt-3 rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
          >
            {soleMember && isOwner ? 'Delete team' : 'Leave team'}
          </button>
        )}
      </section>
    </>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Domains (owner-only, read-mostly post-2026-05-16)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Post-2026-05-16 the domain surface is:
 *   - Read-only list of registered domains (populated by the signup
 *     trigger on first corporate signup). No add/remove from the client;
 *     ownership is immutable once claimed.
 *   - One team-level toggle — `auto_join_by_domain` — gating whether
 *     subsequent same-domain signups auto-join (true) or land in
 *     Pending requests (false). Owner-only.
 */
function DomainsSection({
  teamId,
  isOwner,
  autoJoinByDomain,
  onToast,
}: {
  teamId: string
  isOwner: boolean
  autoJoinByDomain: boolean
  onToast: (msg: string) => void
}) {
  const { domains, loading } = useTeamDomains(teamId)
  const setAutoJoin = useSetTeamAutoJoin(teamId)

  const handleToggle = async () => {
    try {
      await setAutoJoin.mutateAsync({ enabled: !autoJoinByDomain })
      onToast(
        !autoJoinByDomain
          ? 'Auto-join enabled — new signups on your domain join automatically'
          : 'Auto-join disabled — new signups will queue as requests',
      )
    } catch (err) {
      onToast(`Failed to update: ${(err as Error).message}`)
    }
  }

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6">
      <h2 className="text-sm font-semibold text-zinc-900">Domains</h2>
      <p className="mt-0.5 text-xs text-zinc-500">
        Signups on these email domains can join your team.
      </p>
      {loading ? (
        <p className="mt-3 text-xs text-zinc-400">Loading...</p>
      ) : domains.length === 0 ? (
        <p className="mt-3 text-xs text-zinc-400">No domains registered.</p>
      ) : (
        <ul className="mt-3 divide-y divide-zinc-100">
          {domains.map((d) => (
            <li key={d.id} className="flex items-center justify-between py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-zinc-900">{d.domain}</p>
                {d.created_at && (
                  <p className="mt-0.5 text-[11px] text-zinc-400">
                    Claimed {new Date(d.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Team-level auto-join toggle. Owner-only: admins can invite and
          triage requests but not reshape the team's identity, per the
          role hierarchy. Non-owners see the current state as a static pill. */}
      <div className="mt-5 border-t border-zinc-100 pt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-zinc-900">
              Auto-join from company domain
            </p>
            <p className="mt-0.5 text-xs text-zinc-500">
              When on, anyone signing up with an email on these domains
              joins this team automatically. When off, they see a
              &ldquo;Request to join&rdquo; button and you get an email.
            </p>
          </div>
          {isOwner ? (
            <label className="inline-flex cursor-pointer flex-shrink-0 items-center gap-2">
              <input
                type="checkbox"
                checked={autoJoinByDomain}
                disabled={setAutoJoin.isPending}
                onChange={handleToggle}
                className="h-4 w-4 rounded border-zinc-300 accent-zinc-900 disabled:opacity-40"
              />
              <span className="text-xs font-medium text-zinc-700">
                {autoJoinByDomain ? 'On' : 'Off'}
              </span>
            </label>
          ) : (
            <span
              className={
                autoJoinByDomain
                  ? 'flex-shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700'
                  : 'flex-shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-600'
              }
            >
              {autoJoinByDomain ? 'On' : 'Off'}
            </span>
          )}
        </div>
      </div>
    </section>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Pending join request row
// ────────────────────────────────────────────────────────────────────────────

/**
 * One row in the Pending requests panel. Kept as a sub-component for the
 * same reason `BulkInviteResultRow` is — the row is non-trivial and the
 * parent component's JSX is already dense.
 */
function JoinRequestRow({
  req,
  disabled,
  onApprove,
  onDecline,
}: {
  req: TeamJoinRequestWithUser
  disabled: boolean
  onApprove: (label: string) => void
  onDecline: (label: string) => void
}) {
  const email = req.user_email ?? null
  const displayName = req.user_display_name?.trim() || null
  const primary = displayName ?? email ?? req.user_id
  const secondary = displayName && email ? email : null
  const label = displayName || email || req.user_id

  return (
    <li className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-zinc-900">{primary}</p>
        <p className="mt-0.5 truncate text-[11px] text-zinc-400">
          {secondary ? `${secondary} · ` : ''}
          {`requested ${relativeTimeFromNow(req.requested_at)}`}
        </p>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        <button
          onClick={() => onDecline(label)}
          disabled={disabled}
          className="rounded-md border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-40"
        >
          Decline
        </button>
        <button
          onClick={() => onApprove(label)}
          disabled={disabled}
          className="rounded-md bg-zinc-900 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-zinc-800 disabled:opacity-40"
        >
          Admit
        </button>
      </div>
    </li>
  )
}

/**
 * Relative time helper. Tiny hand-rolled version — we don't pull in
 * date-fns for a single format point. Handles minute / hour / day
 * granularity; anything older than 30 days falls back to an absolute
 * date. Covers the realistic range for a pending request (expires_at
 * is 14 days post-request by default).
 */
function relativeTimeFromNow(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(ms / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  return new Date(iso).toLocaleDateString()
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function buildInviteUrl(token: string): string {
  // The `?invite=<token>` handler lives at the app's root — see App.tsx.
  // encodeURIComponent the token: although DB-issued tokens today use a
  // URL-safe alphabet, defence-in-depth treats them as untrusted strings
  // so a future migration that stores raw base64 (with `+` / `/` / `=`)
  // doesn't silently break acceptance (+ decodes to space, = gets mangled
  // by some clipboard sanitisers). Cheap and reverses correctly on the
  // acceptance path, which reads URLSearchParams.get('invite') — already
  // decoded.
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return `${origin}/?invite=${encodeURIComponent(token)}`
}

async function copyInviteLink(token: string): Promise<void> {
  const url = buildInviteUrl(token)
  try {
    await navigator.clipboard.writeText(url)
  } catch {
    // Clipboard API can reject in non-secure contexts; fall back to a prompt
    // so the owner can at least grab the URL manually.
    window.prompt('Copy this invite link:', url)
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Bulk invite helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * Extract every email-looking substring from arbitrary pasted text, lowercase
 * and dedupe in-order.
 *
 * Deliberately permissive — accepts commas, newlines, spaces, `Name <email>`
 * shapes, and any prose around the addresses. Full RFC 5322 validation isn't
 * worth it: the server re-validates via `invite_team_member`'s `invalid_email`
 * branch, and the regex here just needs to recognise the common shapes users
 * actually paste (Gmail address books, CSVs, Slack dumps).
 */
function parseEmailsFromText(text: string): string[] {
  // Conservative regex: allow standard local-part chars plus `+` (subaddress),
  // require at least one `.` in the domain, cap TLD at 24 chars so we don't
  // match e.g. prose punctuation.
  const pattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,24}/g
  const matches = text.match(pattern) ?? []
  const seen = new Set<string>()
  const unique: string[] = []
  for (const m of matches) {
    const normalized = m.toLowerCase()
    if (!seen.has(normalized)) {
      seen.add(normalized)
      unique.push(normalized)
    }
  }
  return unique
}

/**
 * Remove a list of emails from arbitrary text without disturbing the
 * surrounding prose. Used after "Send invites" to clear the parsed addresses
 * from the textarea while preserving half-typed content (e.g. "Also let me
 * find the CTO's email — " stays put).
 *
 * Matches case-insensitively because pasted content is inconsistent. We
 * escape regex metachars in case emails contain `+` or `.` (they will).
 */
function stripEmailsFromText(text: string, emails: string[]): string {
  if (emails.length === 0) return text
  const escaped = emails.map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')
  // Also eat a trailing comma + optional whitespace or a trailing newline
  // after the email, so removing "alice@acme.com, " doesn't leave a stray
  // comma floating. `\s*` catches the comma cleanup in common cases.
  const pattern = new RegExp(`(?:${escaped})\\s*,?\\s*`, 'gi')
  return text.replace(pattern, '').trim()
}

/**
 * Map a raw RPC error code to a short, human-readable label.
 *
 * The RPC `invite_team_member` raises exceptions with machine-readable codes
 * (`not_team_owner`, `invalid_email`, `already_member`, `duplicate_pending`).
 * Postgres passes those through as the Error.message. We translate to
 * friendly copy for the results list; unknown codes fall through to the raw
 * string so debugging stays possible.
 */
function friendlyInviteError(code: string | null): string {
  if (!code) return 'Something went wrong'
  const lower = code.toLowerCase()
  // Post-2026-05-15 the invite RPC raises `not_team_admin_or_owner`. Older
  // callers and retries may still see `not_team_owner` from mixed-version
  // surfaces (other RPCs that didn't widen); both mean "your role can't
  // invite from this UI".
  if (lower.includes('not_team_admin_or_owner')) return 'You need admin or owner role to invite'
  if (lower.includes('not_team_owner')) return 'You need admin or owner role to invite'
  if (lower.includes('invalid_email')) return 'Invalid email address'
  if (lower.includes('already_member')) return 'Already a team member'
  if (lower.includes('duplicate_pending')) return 'Already has a pending invite'
  // 2026-05-16 domain gate: server rejects invites whose email domain
  // isn't registered on the team. The bulk-invite path pre-filters these
  // client-side, but single-invite + race conditions can still surface it.
  if (lower.includes('invite_domain_not_registered')) {
    return 'Can only invite people on a registered team domain'
  }
  return code
}

// ────────────────────────────────────────────────────────────────────────────
// Role rendering helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * Compact role chip for the member list. Owner = filled zinc (authoritative),
 * admin = amber (elevated privilege, visually louder than "member" so it
 * stands out in long rosters), member = quiet zinc outline.
 */
function RolePill({ role }: { role: TeamRole }) {
  const tone =
    role === 'owner'
      ? 'bg-zinc-900 text-white'
      : role === 'admin'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-zinc-100 text-zinc-600'
  const label = role === 'owner' ? 'Owner' : role === 'admin' ? 'Admin' : 'Member'
  return (
    <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}>
      {label}
    </span>
  )
}

/**
 * "You're an owner" / "You're an admin" / "You're a member" — handles the
 * a/an article correctly so the header copy reads naturally.
 */
function roleArticleLabel(role: TeamRole): string {
  if (role === 'owner') return 'an owner'
  if (role === 'admin') return 'an admin'
  return 'a member'
}

function BulkInviteResultRow({
  row,
  onCopyLink,
}: {
  row: BulkInviteRow
  onCopyLink: () => void
}) {
  const derivedName = nameFromEmail(row.email)
  const primary = derivedName ?? row.email
  const secondary = derivedName ? row.email : null

  // Status chip — tiny pill on the right of each row. Keeps the row
  // scannable when the list is long.
  let chip: { label: string; tone: 'zinc' | 'green' | 'amber' | 'red' } | null = null
  if (row.status === 'pending') chip = { label: 'Queued', tone: 'zinc' }
  else if (row.status === 'sending') chip = { label: 'Sending…', tone: 'zinc' }
  else if (row.status === 'emailed') chip = { label: 'Emailed', tone: 'green' }
  // `sent` means the invitation row was created but the email failed —
  // copy-link fallback is the intended recovery. Amber signals "partial".
  else if (row.status === 'sent') chip = { label: 'Link only', tone: 'amber' }
  else if (row.status === 'error') chip = { label: friendlyInviteError(row.errorCode), tone: 'red' }

  const toneClasses: Record<'zinc' | 'green' | 'amber' | 'red', string> = {
    zinc: 'bg-zinc-100 text-zinc-600',
    green: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-700',
  }

  return (
    <li className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-zinc-900">{primary}</p>
        {secondary && (
          <p className="mt-0.5 truncate text-[11px] text-zinc-400">{secondary}</p>
        )}
        {row.status === 'sent' && row.emailError && (
          // Show the specific email-layer failure so the operator knows
          // whether to retry or fall back to the copy-link path.
          <p className="mt-0.5 truncate text-[11px] text-amber-700">
            Email failed: {row.emailError}
          </p>
        )}
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        {chip && (
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${toneClasses[chip.tone]}`}
          >
            {chip.label}
          </span>
        )}
        {/* Copy link only when we have one — both "emailed" (belt-and-braces)
            and "sent" (primary recovery path) cases. */}
        {row.token && (row.status === 'emailed' || row.status === 'sent') && (
          <button
            type="button"
            onClick={onCopyLink}
            className="rounded-md border border-zinc-200 px-2 py-0.5 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
          >
            Copy link
          </button>
        )}
      </div>
    </li>
  )
}
